import {
  Stack,
  Text,
  Image,
  HStack,
  Icon,
  Button,
  Heading,
} from '@chakra-ui/react';
import { Broker, NestedHome } from '@habity/types';
import { getTitle } from '@habity/services/brokers';
import { BrokerCardButtonVariant } from './components/Buttons';
import {
  getAvatarTransformOptions,
  getAvatarUrl,
} from '~/lib/providers/supabase/avatars';
import { createClient } from '~/lib/providers/supabase/client';
import { ListSkeleton } from '~/components/homes/list/components/ListSkeleton';
import { Link } from '@chakra-ui/next-js';
import { ICONS } from '~/constants';
import { getMunicipalityPath } from '@habity/services/municipalities';
import { getBrokerPath, getBrokerNameParts, getStreet } from '@habity/services';
import { useAppStore } from '~/store';
import { useTranslations } from 'next-intl';

type BrokerProfileProps = {
  broker: Broker;
  home?: NestedHome;
  homeRelation?: 'responsibleBroker' | 'additionalContact';
  isLoading?: boolean;
  buttonVariant?: BrokerCardButtonVariant;
  showButtons?: boolean;
  showDescription?: boolean;
  customTitle?: string;
  onClick?: () => void;
};

export const BrokerCardProfile: React.FC<BrokerProfileProps> = ({
  broker,
  home,
  homeRelation,
}) => {
  const { id, name, email, phone, office } = broker || {};
  const t = useTranslations();
  const title = getTitle(broker);
  const isMobile = useAppStore((state) => state.isMobile);

  const supabase = createClient();

  const avatarUrl = getAvatarUrl(
    supabase,
    'brokers',
    id,
    '.png',
    getAvatarTransformOptions('homeProfile'),
    email,
    broker?.cloudflare_image_id
  );

  const { firstName, lastName } = getBrokerNameParts(name);

  return (
    <Stack spacing="3">
      <Link href={getBrokerPath(broker)} _hover={{ textDecoration: 'none' }}>
        <Stack spacing="3">
          {/* Title, name, image */}
          <Stack spacing="0.5">
            <Text
              size={{ mobile: 'sm', desktop: 'md' }}
              fontWeight="light"
              color="blackAlpha.800"
            >
              {homeRelation ? t(`HomeProfile.Brokers.${homeRelation}`) : title}
            </Text>

            <Heading size="xl" wordBreak="break-word">
              <span style={{ whiteSpace: 'nowrap' }}>{firstName}</span>{' '}
              <span style={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                {lastName}
              </span>
            </Heading>
          </Stack>

          <Image
            backgroundColor="palePeach"
            maxH="250px"
            objectFit="cover"
            objectPosition="top"
            loading="lazy"
            srcSet={`${avatarUrl} 2x, ${avatarUrl} 1x`}
            src={avatarUrl}
            fallback={<ListSkeleton isLoaded={false} w="100%" h="100%" />}
            title={title}
          />
        </Stack>
      </Link>

      {/* Contact info */}
      <HStack justifyContent="space-between" alignItems="flex-start">
        {/* Contact info */}
        <Stack gap="1" fontSize="sm" fontWeight="light" color="blackAlpha.800">
          <Link href={`tel:${phone}`}>
            <HStack alignItems="center" flexShrink="0">
              <Icon as={ICONS.PHONE} boxSize="4" color="blackAlpha.700" />
              <Text size="sm" fontWeight="light" flexShrink="0">
                {phone}
              </Text>
            </HStack>
          </Link>
          <Link href={`mailto:${email}?subject=Fråga till mäklare`}>
            <HStack alignItems="center" flexShrink="0">
              <Icon as={ICONS.MAIL} boxSize="4" color="blackAlpha.700" />
              <Text size="sm" fontWeight="light" flexShrink="0">
                {email}
              </Text>
            </HStack>
          </Link>
        </Stack>

        {/* Location: municipality name */}
        {broker.municipality?.name && (
          <Stack>
            <Link href={getMunicipalityPath(broker.municipality)}>
              <HStack alignItems="center" flexShrink="0">
                <Icon
                  as={ICONS.LOCATION_PIN}
                  boxSize="4"
                  color="blackAlpha.700"
                />
                <Text size="sm" fontWeight="light" flexShrink="0">
                  {broker.municipality.name}
                </Text>
              </HStack>
            </Link>
          </Stack>
        )}
      </HStack>

      {/* CTAs */}
      <HStack mt="4">
        <Link
          href={
            isMobile
              ? `sms:${phone || ''}&body=${
                  home ? `Ang ${getStreet(home)}` : 'Hej'
                }%0D%0A%0D%0A`
              : `mailto:${email || ''}?subject=${
                  home ? `Ang ${getStreet(home)}` : 'Fråga till mäklare'
                }&body=Hej ${firstName || ''},%0D%0A%0D%0AJag ${
                  home ? `är intresserad av ${getStreet(home)} och ` : ''
                }har en fråga.%0D%0A%0D%0AMed vänliga hälsningar`
          }
          flex="1"
        >
          <Button
            variant="solid"
            // leftIcon={isMobile ? <ICONS.SMS /> : <ICONS.MAIL />}
            width="100%"
            size="sm"
            fontSize="sm"
            flex="1"
          >
            Ställ en fråga
          </Button>
        </Link>
        <Link href={getBrokerPath(broker)} flex="1">
          <Button
            variant="secondary"
            width="100%"
            size="sm"
            fontSize="sm"
            flex="1"
          >
            Visa mäklarprofil
          </Button>
        </Link>
      </HStack>
    </Stack>
  );
};
